import { logger } from '@rhg/libs/logger';
import { useRouter } from 'next/router';
import { useEffect } from 'react';

const RouteChangeRenderTracking = () => {
    const router = useRouter();

    useEffect(() => {
        // When a shallow route change (e.g. the search URL update in SearchProvider)
        // is cancelled mid-flight by a real page navigation (e.g. clicking a program
        // card), Next.js emits routeChangeError for the cancelled shallow route.
        // In Firefox/Safari the shallow replace is slower (competing with the audio
        // event loop), so by the time the user clicks a card the replace hasn't
        // finished — the card's push() cancels it, and then the shallow routing
        // machinery can roll back the URL, swallowing the real navigation.
        // Detecting the cancellation and retrying the last non-shallow push
        // ensures the intended navigation completes.
        let lastPushUrl: string | null = null;

        const onStart = (url: string, { shallow }: { shallow: boolean }) => {
            // Only track non-shallow navigation (real page changes).
            // Shallow changes come from the search URL update and should not
            // be retried if cancelled.
            if (!shallow) {
                lastPushUrl = url;
            }
        };
        const onComplete = () => {
            lastPushUrl = null;
        };
        const onError = (err: Error & { cancelled?: boolean }, url: string) => {
            if (
                err.cancelled === true &&
                lastPushUrl !== null &&
                url === lastPushUrl
            ) {
                // Our push was directly cancelled by a competing navigation.
                // Retry it on the next tick.
                const retryUrl = lastPushUrl;
                lastPushUrl = null;
                setTimeout(() => {
                    router
                        .push(retryUrl)
                        .catch((err) =>
                            logger.error(
                                'Failed to retry navigation',
                                {},
                                err as Error
                            )
                        );
                }, 0);
            }
        };

        router.events.on('routeChangeStart', onStart);
        router.events.on('routeChangeComplete', onComplete);
        router.events.on('routeChangeError', onError);
        return () => {
            router.events.off('routeChangeStart', onStart);
            router.events.off('routeChangeComplete', onComplete);
            router.events.off('routeChangeError', onError);
        };
    }, [router]);

    return null;
};

export default RouteChangeRenderTracking;
